import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { date, mixed, number, object, string } from 'yup';

import { useToast } from 'Component/Alerta';
import BotaoLink from 'Component/BotaoLink';
import Cabecalho from 'Component/Cabecalho';
import Form, { Arquivo, Botao, Campo, Selecao } from 'Component/Form';
import Icone from 'Component/Icone';
import LayoutDefault from 'Component/Layout';
import Empresa from 'Interface/Empresas';
import { useRecuperarUsuariosQuery } from 'Service';
import {
  ESTADOS,
  IMAGENS,
  MASCARA_CEP,
  MASCARA_CNPJ,
  MASCARA_TELEFONE,
} from 'View/constants';
import { useAdicionarEmpresaMutation } from 'View/Service/Default/Empresas';
import { useRecuperarCepQuery } from 'View/Service/viacep';

const regras = object({
  nome_fantasia: string().required('Obrigatório'),
  razao_social: string().required('Obrigatório'),
  cnpj: string().nullable().required('Obrigatório'),
  nire: string().nullable().optional(),
  inscricao_municipal: string().nullable().optional(),
  inscricao_estadual: string().nullable().optional(),
  inscricao_jucesp: string().nullable().optional(),
  responsavel_legal: string().nullable().optional(),
  abertura: date().nullable().optional(),
  telefone1: string().required('Obrigatório'),
  telefone2: string().nullable().optional(),
  cep: string().nullable().optional(),
  estado: string().optional(),
  cidade: string().optional(),
  bairro: string().optional(),
  endereco: string().optional(),
  numero: string().nullable().optional(),
  complemento: string().nullable().optional(),
  email_para_contato: string().nullable().optional(),
  site: string().nullable().optional(),
  imagem: mixed()
    .test('tamanho', 'O arquivo é muito grande', (valor) => {
      if (valor && valor.size) {
        return valor.size <= 500000;
      }
      return true;
    })
    .test('tipo', 'Formato não suportado', (valor) => {
      if (valor && valor.type) {
        return IMAGENS.includes(valor.type);
      }
      return true;
    })
    .optional(),
  responsavel_rh_id: number().nullable().optional(),
});

const Adicionar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const [cep, setCep] = useState<string>('');
  const [adicionarEmpresa] = useAdicionarEmpresaMutation();
  const { data: endereco } = useRecuperarCepQuery(cep, {
    skip: cep && cep.length == 9 ? false : true,
  });

  const { data: { usuarios } = {} } = useRecuperarUsuariosQuery({
    finder: 'listaJson',
    sort: 'nome',
    limit: 200,
  });

  const enviar = async (dados: Empresa): Promise<void> => {
    try {
      await adicionarEmpresa(dados).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });

      navegacao('/empresas');
    } catch (error) {
      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Adicionando empresa"
        descricao="Preencha o formulário e clique em enviar para adicionar a empresa"
      />
      <Form onSubmit={enviar} regras={regras}>
        <div className="row">
          <div className="col-md-9">
            <div className="bg-white shadow-sm rounded p-4">
              <div className="row">
                <div className="col-md-12">
                  <Campo name="nome_fantasia" rotulo="Nome fantasia" />
                </div>
                <div className="col-md-12">
                  <Campo name="razao_social" rotulo="Razão Social" />
                </div>
                <div className="col-md-6">
                  <Campo name="cnpj" rotulo="CNPJ" mascara={MASCARA_CNPJ} />
                </div>
                <div className="col-md-6">
                  <Campo name="nire" rotulo="NIRE" />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="inscricao_municipal"
                    rotulo="Inscrição municipal"
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="inscricao_estadual"
                    rotulo="Inscrição estadual"
                  />
                </div>
                <div className="col-md-4">
                  <Campo name="inscricao_jucesp" rotulo="Inscrição JUCESP" />
                </div>
                <div className="col-md-12">
                  <Campo name="responsavel_legal" rotulo="Responsável legal" />
                </div>
                <div className="col-md-4">
                  <Campo
                    type="date"
                    name="abertura"
                    rotulo="Data de abertura"
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="telefone1"
                    rotulo="Telefone 1"
                    mascara={MASCARA_TELEFONE}
                    prefixar={<Icone nome="telephone-fill" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="telefone2"
                    rotulo="Telefone 2"
                    mascara={MASCARA_TELEFONE}
                    prefixar={<Icone nome="telephone-fill" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="cep"
                    rotulo="CEP"
                    prefixar={<Icone nome="geo-fill" />}
                    aoAtualizar={setCep}
                    mascara={MASCARA_CEP}
                  />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="estado"
                    rotulo="Estado onde reside"
                    opcoes={ESTADOS}
                    forcarValor={endereco?.uf}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="cidade"
                    rotulo="Cidade onde reside"
                    forcarValor={endereco?.localidade}
                  />
                </div>
                <div className="col-md-3">
                  <Campo
                    name="bairro"
                    rotulo="Bairro onde reside"
                    forcarValor={endereco?.bairro}
                  />
                </div>
                <div className="col-md-7">
                  <Campo
                    name="endereco"
                    rotulo="Endereço de onde reside"
                    forcarValor={endereco?.logradouro}
                  />
                </div>
                <div className="col-md-2">
                  <Campo name="numero" type="number" rotulo="Número" />
                </div>
                <div className="col-md-12">
                  <Campo name="complemento" rotulo="Complemento" />
                </div>
                <div className="col-md-6 mb-3 mb-sm-0">
                  <Campo
                    name="email_para_contato"
                    rotulo="E-mail para contato"
                    prefixar={<Icone nome="envelope-fill" />}
                  />
                </div>
                <div className="col-md-6 mb-0">
                  <Campo
                    name="site"
                    rotulo="Site"
                    prefixar={<Icone nome="link" />}
                  />
                </div>
                <div className="col-md-12">
                  <Selecao
                    name="responsavel_rh_id"
                    rotulo="Responsável pelo RH"
                    opcoes={usuarios}
                    semMargem
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="bg-white shadow-sm rounded p-4">
              <Arquivo name="imagem" />

              <div className="mt-5">
                <Botao>Enviar</Botao>
                <BotaoLink tema="light" to="/clientes" className="ms-2">
                  Cancelar
                </BotaoLink>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </LayoutDefault>
  );
};

export default Adicionar;
